import React, { useState } from 'react';
import CachedImg from '../../../components/CachedImg';
import Lightbox from './Lightbox';
import { categoriaDeTipo } from './metricas';
import type { Caso } from './tipos';
import { CHIP, COLOR_CATEGORIA, ChipMotivo, ChipTipoCaso, formatearFecha } from './ui';

/**
 * Tarjeta de un caso del dataset: generada + fotos de producto + instrucciones de
 * edición + flags. La usan las tres sub-vistas; lo que cambia entre ellas
 * (checkbox, resultado, veredicto) entra por `children`.
 *
 * Si la IA clasificó el motivo de las ediciones (`motivo_edicion`), va el chip en la
 * fila de arriba y, salvo en `compacto`, la razón VISIBLE debajo de las ediciones (nada
 * depende del hover). Se ve en las tres sub-vistas: es informativo.
 *
 * Radios: la tarjeta es rounded-2xl (16) con p-3 (12) → las imágenes adentro van
 * con rounded (4): exterior = interior + padding.
 */
const CasoCard: React.FC<{ caso: Caso; children?: React.ReactNode; className?: string; compacto?: boolean }> = ({
  caso,
  children,
  className = '',
  compacto = false,
}) => {
  const [ampliada, setAmpliada] = useState<string | null>(null);
  const miniatura = caso.generacion.url ?? caso.generacion.url_full;
  const full = caso.generacion.url_full ?? caso.generacion.url;
  const fotos = [...caso.producto.fotos].sort((a, b) => Number(b.principal) - Number(a.principal)).slice(0, 4);
  const flagsActivos = caso.flags
    ? (Object.entries(caso.flags) as Array<[string, boolean]>).filter(([, v]) => v).map(([k]) => k)
    : [];
  const nombreProducto = [caso.producto.nombre, caso.producto.variante_nombre].filter(Boolean).join(' · ');

  return (
    <div className={`flex gap-3 rounded-2xl border border-gray-200 bg-white p-3 ${className}`}>
      <div className={`flex shrink-0 flex-col gap-2 ${compacto ? 'w-20' : 'w-32'}`}>
        {miniatura ? (
          <button type="button" onClick={() => full && setAmpliada(full)} className="block" title="Ampliar la generada">
            <CachedImg src={miniatura} alt="" className={`aspect-[4/5] rounded bg-gray-100 object-cover ${compacto ? 'w-20' : 'w-32'}`} />
          </button>
        ) : (
          <div className={`flex aspect-[4/5] items-center justify-center rounded bg-gray-100 text-[11px] text-gray-400 ${compacto ? 'w-20' : 'w-32'}`}>
            sin imagen
          </div>
        )}
        {/* En compacto (columna w-20) van en 2 columnas de 36 px: 2×36 + 4 de gap = 76 ≤ 80. */}
        <div className={compacto ? 'grid grid-cols-2 gap-1' : 'flex flex-wrap gap-1'} title="Fotos del producto (las referencias)">
          {fotos.map((f, i) => (
            <button
              key={`${f.url}-${String(i)}`}
              type="button"
              onClick={() => setAmpliada(f.url_full ?? f.url)}
              title={f.vista ?? (f.principal ? 'principal' : 'foto de producto')}
            >
              <CachedImg src={f.url} alt="" className={`${compacto ? 'h-9 w-9' : 'h-7 w-7'} rounded object-cover ${f.principal ? 'ring-1 ring-brand-black' : ''}`} />
            </button>
          ))}
          {fotos.length === 0 && <span className={`text-[11px] text-red-600 ${compacto ? 'col-span-2' : ''}`}>sin fotos de producto</span>}
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <ChipTipoCaso tipo={caso.tipo_caso} />
          {caso.prioridad !== null && (
            <span className={`${CHIP} ${caso.prioridad === 1 ? 'bg-brand-black text-white' : 'bg-gray-200 text-gray-700'}`}>
              P{caso.prioridad}
            </span>
          )}
          {caso.motivo_edicion && <ChipMotivo motivo={caso.motivo_edicion} />}
          {flagsActivos.map((f) => (
            <span key={f} className={`${CHIP} border border-gray-300 bg-white text-brand-black`}>
              {f}
            </span>
          ))}
          <span className="ml-auto text-[11px] text-gray-500">
            {caso.generacion.modo_utilizado ?? 'modo ?'}
            {caso.generacion.formato ? ` · ${caso.generacion.formato}` : ''} · {formatearFecha(caso.generacion.created_at)}
          </span>
        </div>
        <div className="truncate text-sm text-brand-black" title={`${caso.empresa.nombre ?? caso.empresa.id_company} — ${nombreProducto}`}>
          <b>{caso.empresa.nombre ?? caso.empresa.id_company.slice(0, 8)}</b>
          {nombreProducto && <span className="text-gray-600"> — {nombreProducto}</span>}
        </div>
        {caso.ediciones.length > 0 ? (
          <ul className="space-y-1">
            {caso.ediciones.map((ed) => (
              <li key={ed.id} className="flex items-start gap-2 text-xs">
                <span className={`${CHIP} shrink-0 bg-gray-100 text-gray-600`}>
                  {ed.modo}
                  {ed.con_mascara ? ' · máscara' : ''}
                </span>
                <span className="italic text-brand-black">{ed.instruction?.trim() || '(sin instrucción)'}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-400">Sin ediciones (control: la descargó y nunca la editó).</p>
        )}
        {caso.motivo_edicion && !compacto && (
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-600">
            <span>
              <b className="text-brand-black">IA:</b> {caso.motivo_edicion.razon?.trim() || '(sin razón)'}
            </span>
            {caso.motivo_edicion.tipos_sospechados.map((t) => (
              <span key={t} title={t} className={`${CHIP} shrink-0 whitespace-nowrap ${COLOR_CATEGORIA[categoriaDeTipo(t)]}`}>
                {t}
              </span>
            ))}
          </div>
        )}
        {children}
      </div>

      {ampliada && <Lightbox url={ampliada} onClose={() => setAmpliada(null)} />}
    </div>
  );
};

export default CasoCard;
